import PageNavbar from '../../components/feature/PageNavbar';
import PageCloudHero from '../../components/feature/PageCloudHero';
import { useTranslation } from 'react-i18next';
import CTA from '../home/components/CTA';
import Footer from '../home/components/Footer';

const ABOUT_PORTRAIT_IMAGE = '/images/kamilfrajtko.webp';

const glassPanelClass =
  'rounded-[32px] border border-white/10 bg-[#1A1A1A] shadow-[0_22px_60px_rgba(0,0,0,0.32)]';

const glassPanelSoftClass =
  'rounded-[28px] border border-white/10 bg-[#1A1A1A] shadow-[0_18px_48px_rgba(0,0,0,0.28)]';

const cardAmbientClass =
  'pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_14%_16%,rgba(163,230,53,0.12),transparent_22%),radial-gradient(circle_at_86%_14%,rgba(64,196,255,0.12),transparent_24%),linear-gradient(145deg,rgba(255,255,255,0.018)_0%,transparent_34%,transparent_72%,rgba(255,255,255,0.01)_100%)]';

const cardAmbientSoftClass =
  'pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_14%_16%,rgba(163,230,53,0.09),transparent_20%),radial-gradient(circle_at_84%_18%,rgba(64,196,255,0.09),transparent_22%),linear-gradient(145deg,rgba(255,255,255,0.014)_0%,transparent_34%,transparent_72%,rgba(255,255,255,0.008)_100%)]';

export default function AboutPage() {
  const { t } = useTranslation();
  const values = t('about.values.items', { returnObjects: true }) as Array<{
    title: string;
    description: string;
  }>;
  const valueIcons = [
    'ri-checkbox-circle-line',
    'ri-focus-3-line',
    'ri-shapes-line',
    'ri-repeat-2-line',
    'ri-message-3-line',
    'ri-rocket-2-line',
  ];
  const focusAreas = t('about.areas.items', { returnObjects: true }) as Array<{
    title: string;
    description: string;
  }>;
  const focusAreaIcons = [
    'ri-window-line',
    'ri-shopping-bag-3-line',
    'ri-flashlight-line',
    'ri-layout-grid-line',
    'ri-brush-line',
    'ri-lifebuoy-line',
  ];
  const whoParagraphs = t('about.who.paragraphs', { returnObjects: true }) as string[];
  const approachParagraphs = t('about.approach.paragraphs', { returnObjects: true }) as string[];
  const cloudsParagraphs = t('about.clouds.paragraphs', { returnObjects: true }) as string[];

  return (
    <div className="relative min-h-screen overflow-hidden bg-[linear-gradient(180deg,#0D0D0D_0%,#12243d_22%,#0D0D0D_52%,#152013_78%,#0D0D0D_100%)] text-white">
      <PageNavbar />

      <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
        <div className="absolute left-[-16rem] top-[18rem] h-[34rem] w-[34rem] rounded-full bg-brand-green/[0.1] blur-3xl" />
        <div className="absolute right-[-14rem] top-[26rem] h-[38rem] w-[38rem] rounded-full bg-brand-ocean/[0.12] blur-3xl" />
        <div className="absolute inset-x-[-10%] top-[44rem] h-[24rem] bg-[radial-gradient(ellipse_at_center,rgba(64,196,255,0.1),transparent_74%)] blur-3xl" />
        <div className="absolute inset-x-[-12%] top-[104rem] h-[26rem] bg-[radial-gradient(ellipse_at_center,rgba(163,230,53,0.08),transparent_76%)] blur-3xl" />
        <div className="absolute right-[8%] bottom-[16rem] h-[24rem] w-[24rem] rounded-full bg-brand-ocean/[0.07] blur-3xl" />
        <div className="absolute left-[4%] bottom-[4rem] h-[22rem] w-[22rem] rounded-full bg-brand-green/[0.06] blur-3xl" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_16%,rgba(163,230,53,0.1),transparent_24%),radial-gradient(circle_at_82%_24%,rgba(64,196,255,0.14),transparent_26%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(163,230,53,0.03)_0%,transparent_28%,transparent_64%,rgba(64,196,255,0.05)_100%)]" />
      </div>

      <div className="relative z-10">
        <PageCloudHero
          badge={t('about.hero.badge')}
          badgeIcon="ri-user-smile-line"
          title={t('about.hero.title')}
          lead={t('about.hero.lead')}
          supporting={t('about.hero.supporting')}
        />

      <section className="relative z-20 -mt-12 pb-24 md:-mt-24">
        <div className="relative z-10 mx-auto max-w-7xl px-6">
          <div className="grid gap-8 lg:grid-cols-[minmax(0,1.12fr)_minmax(320px,0.88fr)]">
            <article className={`relative overflow-hidden ${glassPanelClass} p-8 md:p-10`}>
              <div className={cardAmbientClass} />
              <div className="relative z-10">
                <span className="mb-5 inline-flex items-center rounded-full border border-white/10 bg-white/[0.05] px-4 py-2 text-sm font-semibold text-[#A3E635]">
                  <span className="mr-2 inline-flex items-center">
                    <i className="ri-user-heart-line text-base" />
                  </span>
                  {t('about.who.badge')}
                </span>

                <h2 className="text-3xl font-caprasimo text-white md:text-4xl">
                  {t('about.who.title')}
                </h2>
                {whoParagraphs.map((paragraph) => (
                  <p key={paragraph} className="mt-5 text-base leading-8 text-white/72 md:text-lg">
                    {paragraph}
                  </p>
                ))}
              </div>
            </article>

            <aside className={`relative overflow-hidden ${glassPanelClass}`}>
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_12%,rgba(163,230,53,0.12),transparent_28%),radial-gradient(circle_at_82%_18%,rgba(64,196,255,0.12),transparent_26%)]" />
              <img
                src={ABOUT_PORTRAIT_IMAGE}
                alt="Bc. Kamil Frajtko"
                className="h-full min-h-[26rem] w-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.04)_0%,rgba(13,13,13,0.04)_45%,rgba(13,13,13,0.58)_100%)]" />
              <div className="absolute inset-x-0 bottom-0 z-10">
                <div className="rounded-t-[20px] bg-black/28 p-4 backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] sm:p-4.5">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#A3E635]">
                    {t('about.portrait.role')}
                  </p>
                  <p className="mt-2 w-full text-[1.35rem] font-caprasimo leading-[1.04] text-white sm:text-[1.65rem]">
                    Bc. Kamil Frajtko
                  </p>
                  <p className="mt-2 w-full text-[0.86rem] leading-5 text-white/68 sm:text-[0.9rem]">
                    {t('about.portrait.description')}
                  </p>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section className="relative pb-24">
        <div className="mx-auto grid max-w-7xl gap-8 px-6 lg:grid-cols-2">
          <article className={`relative overflow-hidden ${glassPanelClass} p-8 md:p-10`}>
            <div className={cardAmbientClass} />
            <div className="relative z-10">
              <span className="mb-5 inline-flex items-center rounded-full border border-white/10 bg-white/[0.05] px-4 py-2 text-sm font-semibold text-[#A3E635]">
                <span className="mr-2 inline-flex items-center">
                  <i className="ri-compass-3-line text-base" />
                </span>
                {t('about.approach.badge')}
              </span>

              <h2 className="text-3xl font-caprasimo text-white md:text-4xl">{t('about.approach.title')}</h2>
              {approachParagraphs.map((paragraph) => (
                <p key={paragraph} className="mt-5 text-base leading-8 text-white/72 md:text-lg">
                  {paragraph}
                </p>
              ))}
            </div>
          </article>

          <article className={`relative overflow-hidden ${glassPanelClass} p-8 md:p-10`}>
            <div className={cardAmbientClass} />
            <div className="pointer-events-none absolute right-[-10%] top-[8%] h-56 w-56 rounded-full bg-brand-ocean/16 blur-3xl" />
            <div className="pointer-events-none absolute left-[-10%] bottom-[-8%] h-48 w-48 rounded-full bg-brand-green/12 blur-3xl" />

            <div className="relative z-10">
              <span className="mb-5 inline-flex items-center rounded-full border border-white/10 bg-white/[0.05] px-4 py-2 text-sm font-semibold text-[#A3E635]">
                <span className="mr-2 inline-flex items-center">
                  <i className="ri-flight-takeoff-line text-base" />
                </span>
                {t('about.clouds.badge')}
              </span>

              <h2 className="text-3xl font-caprasimo text-white md:text-4xl">
                {t('about.clouds.title')}
              </h2>
              {cloudsParagraphs.map((paragraph) => (
                <p key={paragraph} className="mt-5 max-w-[42rem] text-base leading-8 text-white/72 md:text-lg">
                  {paragraph}
                </p>
              ))}
            </div>
          </article>
        </div>
      </section>

      <section className="relative py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-12 text-center">
            <h2 className="text-4xl font-caprasimo text-white md:text-5xl">{t('about.values.title')}</h2>
            <p className="mx-auto mt-4 max-w-3xl text-base leading-8 text-white/62 md:text-lg">
              {t('about.values.description')}
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {values.map((value, index) => (
              <article
                key={value.title}
                className={`relative overflow-hidden ${glassPanelSoftClass} p-7 transition-all duration-300 hover:-translate-y-1 hover:border-brand-green/24`}
              >
                <div className={cardAmbientSoftClass} />
                <div className="relative z-10">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/[0.06] text-[#A3E635] shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
                    <i className={`${valueIcons[index] ?? 'ri-star-line'} text-2xl`} />
                  </div>
                  <h3 className="mt-6 text-2xl font-caprasimo text-white">{value.title}</h3>
                  <p className="mt-4 text-sm leading-7 text-white/66 md:text-base">{value.description}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="relative py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-12 text-center">
            <h2 className="text-4xl font-caprasimo text-white md:text-5xl">{t('about.areas.title')}</h2>
            <p className="mx-auto mt-4 max-w-3xl text-base leading-8 text-white/62 md:text-lg">
              {t('about.areas.description')}
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {focusAreas.map((area, index) => (
              <article
                key={area.title}
                className={`relative overflow-hidden ${glassPanelSoftClass} p-7 transition-all duration-300 hover:-translate-y-1 hover:border-brand-green/24`}
              >
                <div className={cardAmbientSoftClass} />
                <div className="relative z-10">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/[0.06] text-[#A3E635] shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
                    <i className={`${focusAreaIcons[index] ?? 'ri-layout-grid-line'} text-2xl`} />
                  </div>
                  <h3 className="mt-6 text-2xl font-caprasimo text-white">{area.title}</h3>
                  <p className="mt-4 text-sm leading-7 text-white/66 md:text-base">{area.description}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      </div>

      <CTA seamless />
      <Footer />
    </div>
  );
}
